import { useCallback, useMemo, useState } from 'react';
import { FiPhone } from 'react-icons/fi';
import type { CountryCode } from 'libphonenumber-js';

import { getPhoneDetails } from '@/shared/utils/phone';
import { VerificationLogoutButton } from '@/shared/components/VerificationLogoutButton';

interface PhoneCollectionOverlayProps {
  currentPhone?: string;
  /** DDI padrão da company (ISO2, ex: 'BR') — usado pra validar números sem código de país. */
  defaultCountry?: string;
  onSubmit: (phone: string) => Promise<void>;
  error?: string;
  isSaving?: boolean;
  onLogout: () => void;
}

/**
 * PhoneCollectionOverlay — Phase C.
 *
 * Mostrado quando o app tem `hasWhatsAppProvider: true` e o usuário não tem
 * telefone cadastrado (ou atualizou o telefone e precisa revalidar).
 * Bloqueia acesso ao app até telefone ser informado.
 *
 * Após submeter, o backend atualiza `user.phone` e marca `phoneVerified=false`.
 * O próximo render do VerificationContext exibe o `VerificationOverlay type='phone'`.
 */
export function PhoneCollectionOverlay({
  currentPhone,
  defaultCountry,
  onSubmit,
  error,
  isSaving,
  onLogout,
}: PhoneCollectionOverlayProps) {
  const [phone, setPhone] = useState(currentPhone ?? '');

  const details = useMemo(
    () => getPhoneDetails(phone, (defaultCountry as CountryCode) || 'BR'),
    [phone, defaultCountry],
  );
  const isValid = !!details?.isValid;
  const showInvalidHint = phone.replace(/\D/g, '').length > 0 && !isValid;

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!details?.isValid) return;
    onSubmit(`${details.countryCallingCode}${details.nationalNumber}`);
  }, [details, onSubmit]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px',
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          position: 'relative',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '32px 28px',
          maxWidth: '420px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
        }}
      >
        {/* type="button" interno do VerificationLogoutButton impede submit do form */}
        <VerificationLogoutButton onLogout={onLogout} disabled={isSaving} />
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: '#f3e8ff',
              marginBottom: '16px',
            }}
          >
            <FiPhone style={{ width: '28px', height: '28px', color: '#8b5cf6' }} />
          </div>
          <h2
            style={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#1e293b',
              margin: 0,
              marginBottom: '8px',
            }}
          >
            Informe seu telefone
          </h2>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
            Vamos enviar um código via WhatsApp para verificar seu número.
          </p>
        </div>

        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="5511999999999"
          autoFocus
          disabled={isSaving}
          data-testid="phone-collection-input"
          style={{
            width: '100%',
            padding: '12px 14px',
            fontSize: '16px',
            borderRadius: '8px',
            border: '1px solid rgba(0, 0, 0, 0.10)',
            backgroundColor: '#ffffff',
            color: '#1e293b',
            outline: 'none',
            marginBottom: '8px',
          }}
          onFocus={(e) => (e.currentTarget.style.borderColor = '#8b5cf6')}
          onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.10)')}
        />
        {showInvalidHint ? (
          <p
            data-testid="phone-collection-invalid-hint"
            style={{ fontSize: '12px', color: '#ef4444', margin: 0, marginBottom: '16px' }}
          >
            Número inválido. Verifique o DDD e o código do país (ex: 5511999999999).
          </p>
        ) : (
          <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0, marginBottom: '16px' }}>
            Digite o número completo com DDD e código do país (ex: 5511999999999).
          </p>
        )}

        {error && (
          <p
            style={{
              fontSize: '13px',
              color: '#ef4444',
              margin: 0,
              marginBottom: '16px',
              padding: '8px 12px',
              backgroundColor: '#fef2f2',
              borderRadius: '6px',
            }}
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={isSaving || !isValid}
          data-testid="phone-collection-submit"
          style={{
            width: '100%',
            padding: '12px 14px',
            fontSize: '15px',
            fontWeight: 600,
            color: '#ffffff',
            backgroundColor: '#8b5cf6',
            border: 'none',
            borderRadius: '8px',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            opacity: !isValid || isSaving ? 0.6 : 1,
          }}
        >
          {isSaving ? 'Salvando...' : 'Continuar'}
        </button>
      </form>
    </div>
  );
}
